// =============================================================================
// Profile — router/composer for both customer and seller profile screens.
// Each tab renders one panel from ./profile/. Panels are independent — they
// fetch their own data, own their own state, and read user/seller via props.
// =============================================================================

import { useState } from "react";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Eyebrow } from "../components/Eyebrow";
import { Icon } from "../components/Icon";
import { supabase } from "../lib/supabase";

import { CustomerOverview } from "./profile/CustomerOverview";
import { SellerOverview } from "./profile/SellerOverview";
import { AccountPanel } from "./profile/AccountPanel";
import { WishlistPanel } from "./profile/WishlistPanel";
import { AddressesPanel } from "./profile/AddressesPanel";
import { PaymentMethodsPanel } from "./profile/PaymentMethodsPanel";
import { SellerPayoutMethodsPanel } from "./profile/SellerPayoutMethodsPanel";
import { StorePanel } from "./profile/StorePanel";
import { PayoutsPanel } from "./profile/PayoutsPanel";
import { SellerReviewsPanel } from "./profile/SellerReviewsPanel";
import { PromotionsPanel } from "./profile/PromotionsPanel";
import { NotificationsPanel } from "./profile/NotificationsPanel";
import { SecurityPanel } from "./profile/SecurityPanel";
import { SettingsPanel } from "./profile/SettingsPanel";
import { SupportPanel } from "./profile/SupportPanel";
import { AboutPanel } from "./profile/AboutPanel";

// Unified profile screen for customer and seller modes. Switches the tab
// list + header card based on `mode`. All data is live from Supabase.
//
// initialTab — caller passes "security" (etc) to deep-link directly to a
// non-default panel. Used by the MFA nudge banner on the seller dashboard
// so "Set up 2FA →" lands on the right place in one click.
export function Profile({ user, seller, mode = "customer", setActive, initialTab, onOpenStore, onSellerCreated, onUserUpdated }) {
  const isSeller = mode === "seller" && !!seller;

  const CUSTOMER_TABS = [
    { id: "overview",      label: "Overview",            icon: "layout-dashboard" },
    { id: "account",       label: "Edit Profile",        icon: "user" },
    { id: "wishlist",      label: "Wishlist",            icon: "heart" },
    { id: "addresses",     label: "Delivery Addresses",  icon: "map-pin" },
    { id: "payments",      label: "Payment Methods",     icon: "credit-card" },
    { id: "notifications", label: "Notifications",       icon: "bell" },
    { id: "security",      label: "Privacy & Security",  icon: "shield" },
    { id: "settings",      label: "Settings",            icon: "settings" },
    { id: "support",       label: "Help & Support",      icon: "help-circle" },
    { id: "about",         label: "About Kapio",         icon: "info" },
  ];

  const SELLER_TABS = [
    { id: "overview",         label: "Overview",            icon: "layout-dashboard" },
    { id: "account",          label: "Edit Profile",        icon: "user" },
    { id: "store",            label: "Store Settings",      icon: "store" },
    { id: "payouts",          label: "Payouts",             icon: "banknote" },
    { id: "payment-methods",  label: "Payout Methods",      icon: "credit-card" },
    { id: "reviews",          label: "Reviews",             icon: "star" },
    { id: "promotions",       label: "Promotions",          icon: "tag" },
    { id: "notifications",    label: "Notifications",       icon: "bell" },
    { id: "security",         label: "Privacy & Security",  icon: "shield" },
    { id: "support",          label: "Help & Support",      icon: "help-circle" },
    { id: "about",            label: "About Kapio",         icon: "info" },
  ];

  const TABS = isSeller ? SELLER_TABS : CUSTOMER_TABS;

  // initialTab override is honored only if the requested id exists in the
  // current TAB list. Otherwise fall back to overview so a stale request
  // never leaves the screen blank.
  const validInitial = initialTab && TABS.some((t) => t.id === initialTab) ? initialTab : "overview";
  const [tab, setTab] = useState(validInitial);

  async function signOut() {
    await supabase.auth.signOut();
  }

  const displayName = isSeller ? seller?.store_name || user?.name : user?.name || "You";
  const avatar = isSeller ? seller?.logo_url : user?.avatar_url;
  const initial = (displayName?.[0] || "K").toUpperCase();
  const roleLabel = isSeller ? "Seller" : user?.role === "admin" ? "Admin" : "Customer";

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <Eyebrow>{isSeller ? "Store account" : "Your account"}</Eyebrow>
        <h1 style={{ fontSize: 26, fontWeight: 900, color: C.charcoal, margin: 0, letterSpacing: "-0.5px" }}>
          {displayName || "You"}
        </h1>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 18, alignItems: "start" }}>
        <div style={{ position: "sticky", top: 80, display: "grid", gap: 4 }}>
          <Card pad={16} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: avatar ? `url(${avatar}) center/cover` : C.hunter,
                  color: C.dust,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  fontWeight: 900,
                  flexShrink: 0,
                }}
              >
                {!avatar && initial}
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: C.charcoal,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayName}
                </div>
                <div style={{ fontSize: 11, color: C.muted }}>{roleLabel}</div>
              </div>
            </div>
          </Card>
          {TABS.map((t) => {
            const on = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  all: "unset",
                  cursor: "pointer",
                  padding: "10px 14px",
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: on ? 800 : 600,
                  color: on ? C.hunter : C.slate,
                  background: on ? C.cream : "transparent",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <Icon name={t.icon} size={14} color={on ? C.hunter : C.slate} />
                {t.label}
              </button>
            );
          })}
          <div style={{ height: 1, background: C.cream, margin: "10px 0" }} />
          <button
            onClick={signOut}
            style={{
              all: "unset",
              cursor: "pointer",
              padding: "10px 14px",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 700,
              color: C.red,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Icon name="log-out" size={14} color={C.red} />
            Sign out
          </button>
        </div>

        <div>
          {tab === "overview" && (isSeller ? (
            <SellerOverview user={user} seller={seller} onGoTo={setTab} setActive={setActive} />
          ) : (
            <CustomerOverview user={user} seller={seller} onGoTo={setTab} setActive={setActive} onOpenStore={onOpenStore} onSellerCreated={onSellerCreated} />
          ))}
          {tab === "account"          && <AccountPanel               user={user} onUserUpdated={onUserUpdated} />}
          {tab === "wishlist"         && <WishlistPanel              user={user} setActive={setActive} />}
          {tab === "addresses"        && <AddressesPanel             user={user} />}
          {tab === "payments"         && <PaymentMethodsPanel        user={user} />}
          {tab === "store"            && <StorePanel                 user={user} seller={seller} />}
          {tab === "payouts"          && <PayoutsPanel               seller={seller} />}
          {tab === "payment-methods"  && <SellerPayoutMethodsPanel   user={user} />}
          {tab === "reviews"          && <SellerReviewsPanel         seller={seller} />}
          {tab === "promotions"       && <PromotionsPanel            seller={seller} />}
          {tab === "notifications"    && <NotificationsPanel         user={user} isSeller={isSeller} setActive={setActive} />}
          {tab === "security"         && <SecurityPanel              user={user} onUserUpdated={onUserUpdated} />}
          {tab === "settings"         && <SettingsPanel              user={user} goTo={setTab} />}
          {tab === "support"          && <SupportPanel               user={user} />}
          {tab === "about"            && <AboutPanel                 />}
        </div>
      </div>
    </div>
  );
}
